import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { TABLES, fetchAvailableTables, fetchTableColumns } from '@/services/supabase';
import type { TablePermission } from '@/types/admin';

// Table context type
interface TableContextType {
  currentTable: string;
  setCurrentTable: (table: string) => void;
  availableTables: { name: string; label: string }[];
  loadingTables: boolean;
  tableColumns: string[];
  loadingColumns: boolean;
  refreshTables: () => Promise<void>;
}

const TableContext = createContext<TableContextType | undefined>(undefined);

interface TableProviderProps {
  children: ReactNode;
  permissions?: TablePermission[];
}

export function TableProvider({ children, permissions = [] }: TableProviderProps) {
  const [currentTable, setCurrentTableState] = useState<string>(TABLES.CALL_RECORDS);
  const [availableTables, setAvailableTables] = useState<{ name: string; label: string }[]>([]);
  const [loadingTables, setLoadingTables] = useState(true);
  const [tableColumns, setTableColumns] = useState<string[]>([]);
  const [loadingColumns, setLoadingColumns] = useState(false);

  // Fetch and filter available tables based on permissions
  const refreshTables = useCallback(async () => {
    setLoadingTables(true);
    
    try {
      const allTables = await fetchAvailableTables();
      
      // If user has permissions, filter tables based on permissions
      // Admins see all tables (empty permissions array)
      // Non-admins only see tables they have permission for
      if (permissions.length > 0) {
        const permittedTableNames = permissions
          .filter(p => p.can_read)
          .map(p => p.table_name);
        
        const filteredTables = allTables.filter(t => 
          permittedTableNames.includes(t.name)
        );
        
        setAvailableTables(filteredTables);
      } else {
        // No permissions - show all tables (for admin or before permissions load)
        setAvailableTables(allTables);
      }
    } catch (error) {
      console.error('Error refreshing tables:', error);
      // Return default table on error
      setAvailableTables([{ name: TABLES.CALL_RECORDS, label: 'Call Records' }]);
    } finally {
      setLoadingTables(false);
    }
  }, [permissions]);

  // Fetch available tables on mount and when permissions change
  useEffect(() => {
    refreshTables();
  }, [refreshTables]);

  // Fetch columns when table changes
  useEffect(() => {
    const loadColumns = async () => {
      setLoadingColumns(true);
      const columns = await fetchTableColumns(currentTable);
      setTableColumns(columns);
      setLoadingColumns(false);
    };
    loadColumns();
  }, [currentTable]);

  const setCurrentTable = useCallback((table: string) => {
    setCurrentTableState(table);
  }, []);

  return (
    <TableContext.Provider
      value={{
        currentTable,
        setCurrentTable,
        availableTables,
        loadingTables,
        tableColumns,
        loadingColumns,
        refreshTables,
      }}
    >
      {children}
    </TableContext.Provider>
  );
}

export function useTable() {
  const context = useContext(TableContext);
  if (context === undefined) {
    throw new Error('useTable must be used within a TableProvider');
  }
  return context;
}
